import { useState, useEffect, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { Breadcrumbs, Button, BannerLarge } from '@loadsmart/loadsmart-ui'
import { Box, Stack, Menu, MenuItem, IconButton } from '@mui/material'
import { MoreVert as MoreVertIcon } from '@mui/icons-material'
import Loading from 'atoms/Loading'
import { removeToken } from 'common/helpers/removeToken'
import ErrorPanel from 'molecules/ErrorPanel'
import { BreadcrumbProps } from '@loadsmart/loadsmart-ui/dist/components/Breadcrumbs'
import GeneralDetails from 'pages/TranslationMaps/components/GeneralDetails'
import MappingDetails from 'pages/TranslationMaps/components/mapping/MappingDetails'
import useTopNavigationContext from 'hooks/useTopNavigationContext/useTopNavigationContext'
import PushableEntityStatusTag from '../../../common/components/development/PusableEntityStatus'
import isEmpty from 'lodash.isempty'
import { useTabTitle } from 'hooks/useTabTitle/useTabTitle'
import IaCComponentReferenceWarning from 'atoms/IaCComponentReferenceWarning/IaCComponentReferenceWarning'
import HistoryComponent from '../components/HistoryComponent'
import {
  TranslationMapDetailsProvider,
  useTranslationMapDetailsContext,
} from '../contexts/TranslationMapDetailsContext'

const TranslationMapDetailsContent = () => {
  const browserHistory = useHistory()
  const {
    translationMap,
    isDirty,
    validations,
    isLoading,
    error,
    isUpdating,
    save,
    export: exportTranslationMap,
    push,
  } = useTranslationMapDetailsContext()

  const [breadCrumbs, setBreadcrumbs] = useState<BreadcrumbProps[]>([
    {
      label: 'Translation maps',
      active: true,
      url: '#',
      onClick: () => {
        browserHistory.push('/translation-maps')
      },
    },
  ])

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const topNavigationContext = useTopNavigationContext()

  const history = useMemo(
    () => (translationMap?.id ? <HistoryComponent id={translationMap?.id} /> : null),
    [translationMap?.id]
  )

  useEffect(() => {
    if (translationMap) {
      setBreadcrumbs(prev => [
        prev[0],
        {
          label: translationMap.name,
        },
      ])
    }
  }, [translationMap])

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  useEffect(() => {
    topNavigationContext.updateState({
      children: (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
          data-testid="translation-map-details-header"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }} gap={2}>
            <Box sx={{ flex: 1 }}>
              <Breadcrumbs entries={breadCrumbs} />
            </Box>

            {translationMap?.status && <PushableEntityStatusTag status={translationMap?.status} />}
            <Button variant="primary" disabled={!isDirty || isUpdating} onClick={save}>
              Save
            </Button>
            <Box>
              <IconButton onClick={handleMenuOpen}>
                <MoreVertIcon />
              </IconButton>
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem
                  onClick={() => {
                    exportTranslationMap()
                    handleMenuClose()
                  }}
                >
                  Export
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    push()
                    handleMenuClose()
                  }}
                >
                  Push
                </MenuItem>
              </Menu>
            </Box>
          </Box>
        </Box>
      ),
    })
  }, [breadCrumbs, isDirty, isUpdating, translationMap, anchorEl, save, exportTranslationMap, push])

  return (
    <Stack spacing={0} sx={{ width: '100%' }}>
      {isLoading && <Loading className="mt-8 justify-center" />}

      {translationMap && translationMap.iac_reference ? <IaCComponentReferenceWarning /> : null}

      {error && <ErrorPanel error={removeToken(JSON.stringify(error, null, 2))} />}
      {!isEmpty(validations) ? <BannerLarge description={validations} variant="danger" /> : null}

      {translationMap && (
        <Stack sx={{ width: '100%' }} spacing={2}>
          {/* General Details and Execution - Top Level */}
          <GeneralDetails />
          <MappingDetails />
        </Stack>
      )}
    </Stack>
  )
}

const TranslationMapDetails = () => {
  useTabTitle('Translation Map')

  return (
    <TranslationMapDetailsProvider>
      <TranslationMapDetailsContent />
    </TranslationMapDetailsProvider>
  )
}
export default TranslationMapDetails
